import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { TrendingUp, Sparkles, CheckCircle2, Eye, XCircle, Loader2 } from "lucide-react";
import { currency } from "@/lib/constants";
import { toast } from "sonner";
import BulkAnalyzerDialog from "@/components/BulkAnalyzerDialog";

const REC_STYLE = {
  buy: { color: "#34d399", label: "BUY", Icon: CheckCircle2 },
  watch: { color: "#fbbf24", label: "WATCH", Icon: Eye },
  skip: { color: "#f87171", label: "SKIP", Icon: XCircle },
};

export default function InvestmentInsightsPage() {
  const [data, setData] = useState(null);
  const [analyzing, setAnalyzing] = useState(null); // null | 'bulk'
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });

  const load = () => api.get("/investments/insights").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const analyzeTop = async (opps) => {
    const batch = opps.slice(0, 5);
    setAnalyzing("bulk");
    setProgress({ done: 0, total: batch.length, current: batch[0]?.title || "" });
    for (let i = 0; i < batch.length; i++) {
      setProgress({ done: i, total: batch.length, current: batch[i].title });
      try { await api.post(`/ai/investment-analysis/${batch[i].id}`); } catch { /* skip */ }
    }
    setProgress({ done: batch.length, total: batch.length, current: "" });
    toast.success(`Analyzed ${batch.length} top opportunities.`);
    await load();
    setAnalyzing(null);
  };

  if (!data) return <div className="text-[#7a6d5a] py-12 text-center">Loading…</div>;

  const scored = data.scored_items || [];
  const baseline = data.top_baseline || [];
  const recs = data.recommendations || [];
  const showList = scored.length > 0 ? scored : baseline;

  return (
    <div className="space-y-6" data-testid="investment-insights-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={14} className="text-[#d4a574]" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] font-semibold">Investment Insights</div>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Underwritten opportunities. Ranked.</h1>
          <p className="text-[13px] text-[#b8ac96] mt-2 max-w-2xl">
            AI-generated investment scores using ROI, cap rate, cash-on-cash, and break-even projections. Recommendations are AI assessments, not investment advice.
          </p>
        </div>
        {scored.length < 5 && (
          <button onClick={() => analyzeTop(baseline)} disabled={analyzing} data-testid="bulk-analyze-btn" className="flex items-center gap-2 px-4 py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[13px] hover:bg-[#e8b988] disabled:opacity-60">
            {analyzing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {analyzing ? `Analyzing ${progress.done + 1}/${progress.total}…` : "Analyze Top 5 with AI"}
          </button>
        )}
        <BulkAnalyzerDialog onComplete={load} />
      </div>

      {analyzing && (
        <div className="border border-[#d4a574]/40 rounded-lg bg-[#241d16] p-4" data-testid="bulk-progress">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-[#d4a574] font-semibold flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Underwriting {progress.done + 1} of {progress.total}</div>
            <div className="text-[10px] font-mono text-[#7a6d5a]">{Math.round((progress.done / progress.total) * 100)}%</div>
          </div>
          <div className="h-1 rounded bg-[#33291f] overflow-hidden">
            <div className="h-full bg-[#d4a574] [transition:width_400ms_ease]" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
          </div>
          <div className="text-[11px] text-[#b8ac96] mt-2 truncate">▶ {progress.current}</div>
        </div>
      )}

      {/* Rec breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="kpi-frame rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold mb-2">Analyzed</div>
          <div className="font-display text-3xl font-bold text-[#d4a574]">{data.total_analyzed}</div>
          <div className="text-[11px] text-[#7a6d5a] mt-1">Deep AI underwrites</div>
        </div>
        {["buy", "watch", "skip"].map((k) => {
          const r = recs.find(x => x.recommendation === k);
          const meta = REC_STYLE[k];
          return (
            <div key={k} className="kpi-frame rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold mb-2 flex items-center gap-1.5">
                <meta.Icon size={11} style={{ color: meta.color }} /> {meta.label}
              </div>
              <div className="font-display text-3xl font-bold" style={{ color: meta.color }}>{r?.count || 0}</div>
              <div className="text-[11px] text-[#7a6d5a] mt-1">
                {r?.avg_score ? `avg ${Math.round(r.avg_score)} score` : "—"} {r?.avg_roi_1yr ? `· ${r.avg_roi_1yr.toFixed(1)}% 1yr ROI` : ""}
              </div>
            </div>
          );
        })}
      </div>

      {/* Table */}
      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] overflow-hidden">
        <div className="p-4 border-b border-[#33291f] flex items-center justify-between">
          <div className="font-display text-lg font-semibold">{scored.length > 0 ? "AI-Underwritten Investments" : "Top-Scored (baseline — not yet underwritten)"}</div>
          {scored.length === 0 && <span className="text-[11px] text-[#7a6d5a]">Click "Analyze Top 5 with AI" to generate deep underwrites.</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="insights-table">
            <thead className="bg-[#241d16] text-[10px] uppercase tracking-wider text-[#7a6d5a]">
              <tr>
                <th className="text-left px-4 py-2.5">Opportunity</th>
                <th className="text-left px-4 py-2.5">Location</th>
                <th className="text-right px-4 py-2.5">Price</th>
                <th className="text-right px-4 py-2.5">Inv. Score</th>
                <th className="text-right px-4 py-2.5">1yr ROI</th>
                <th className="text-right px-4 py-2.5">Cap Rate</th>
                <th className="text-center px-4 py-2.5">Rec.</th>
              </tr>
            </thead>
            <tbody>
              {showList.slice(0, 20).map((o) => {
                const ia = o.investment_analysis;
                const rec = ia?.recommendation ? REC_STYLE[ia.recommendation] : null;
                return (
                  <tr key={o.id} className="border-t border-[#33291f] hover:bg-[#241d16]" data-testid={`insight-row-${o.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/app/opportunities/detail/${o.id}`} className="font-semibold hover:text-[#d4a574] line-clamp-1">{o.title}</Link>
                      <div className="text-[10px] text-[#7a6d5a] uppercase tracking-wider">{o.category?.replace(/_/g, " ")}</div>
                    </td>
                    <td className="px-4 py-3 text-[#b8ac96]">{o.city}, {o.state}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#d4a574]">{currency(o.price)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold" style={{ color: rec?.color || "#f4ede2" }}>{ia?.investment_score ? Math.round(ia.investment_score) : `${Math.round(o.ai_score?.opportunity || 0)}*`}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#34d399]">{ia?.roi_1yr_pct ? `${ia.roi_1yr_pct.toFixed(1)}%` : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">{ia?.cap_rate_pct ? `${ia.cap_rate_pct.toFixed(1)}%` : "—"}</td>
                    <td className="px-4 py-3 text-center">
                      {rec ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider" style={{ color: rec.color, borderColor: rec.color, border: "1px solid" }}>
                          <rec.Icon size={10} /> {rec.label}
                        </span>
                      ) : <span className="text-[10px] text-[#7a6d5a] uppercase font-mono">not analyzed</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {scored.length === 0 && (
          <div className="p-3 text-[10px] text-[#7a6d5a] border-t border-[#33291f] font-mono">
            * Baseline AI Opportunity Score (from continuous scoring pass). Deep investment underwriting requires the "Analyze Top 5 with AI" action or per-opportunity "Run AI Investment Analysis" on the detail page.
          </div>
        )}
      </div>
    </div>
  );
}
